import React, { useRef, useState } from 'react';
import {X, Calculator, Eraser, Download, Waves, Info, ExternalLink} from 'lucide-react';
import html2canvas from 'html2canvas';

interface FlujoGradualmenteVariadoCalculatorProps {
  onClose: () => void;
}

interface Inputs {
  Q: string;
  B: string;
  K: string;
  material: string;
  n: string;
  So: string;
  useTirRef: boolean;
  Tir: string;
}

interface Results {
  yn: number;
  yc: number;
  regimen: string;
  perfil: string;
  so: number;
}

const MATERIALS: { label: string; value: string }[] = [
  { label: 'Concreto (Acabado con llana metálica)', value: '0.013' },
  { label: 'Concreto (Acabado con llana de madera)', value: '0.015' },
  { label: 'Concreto (Acabado, con grava en el fondo)', value: '0.017' },
  { label: 'Concreto (Sin acabado)', value: '0.017' },
  { label: 'Cemento', value: '0.013' },
  { label: 'Metal', value: '0.012' },
  { label: 'Madera', value: '0.012' },
  { label: 'Ladrillo', value: '0.015' },
  { label: 'Mampostería de piedra', value: '0.025' },
  { label: 'Piedra Labrada', value: '0.015' },
  { label: 'Asfalto liso', value: '0.013' },
  { label: 'Asfalto Rugoso', value: '0.016' },
  { label: 'Cubierta Vegetal', value: '0.030' },
  { label: 'Tierra', value: '0.022' },
  { label: 'Hierba densa', value: '0.080' },
  { label: 'Acrílico', value: '0.009' }
];

const initialInputs: Inputs = {
  Q: '',
  B: '',
  K: '',
  material: '',
  n: '',
  So: '',
  useTirRef: true,
  Tir: ''
};

const FlujoGradualmenteVariadoCalculator: React.FC<FlujoGradualmenteVariadoCalculatorProps> = ({ onClose }) => {
  const [inputs, setInputs] = useState<Inputs>(initialInputs);
  const [results, setResults] = useState<Results | null>(null);
  const [error, setError] = useState<string | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  const handleChange = (field: keyof Inputs, value: string | boolean) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleMaterialChange = (value: string) => {
    setInputs(prev => ({ ...prev, material: value, n: value }));
  };

  const calculate = () => {
    setError(null);
    setResults(null);

    const q = Number(inputs.Q);
    const b = Number(inputs.B);
    const k = Number(inputs.K);
    const n = Number(inputs.n);
    const s = Number(inputs.So);
    const vtir = Number(inputs.Tir);
    const useRef = inputs.useTirRef;

    if (!isFinite(q) || !isFinite(n) || !isFinite(b) || !isFinite(k) || !isFinite(s)) {
      setError('Faltan datos o son irreales');
      return;
    }
    if (q <= 0 || n <= 0 || b < 0 || k < 0) {
      setError('Faltan datos o son irreales');
      return;
    }
    if (b === 0 && k === 0) {
      setError('No existe una sección con esas características');
      return;
    }
    if (useRef && (!isFinite(vtir) || vtir <= 0)) {
      setError('Tiene marcada la casilla "Tirante de referencia" pero no ha ingresado un valor válido');
      return;
    }

    const theta = Math.atan(s);
    const so = Math.sin(theta);

    let yn = 0;
    let yc = 0;
    let regimen = '';
    let perfil = '';

    try {
      if (so > 0) {
        // ---- Sección triangular (b == 0) ----
        if (b === 0) {
          yc = Math.pow(
            (Math.pow(q, 2) * 2) / (Math.pow(k, 2) * 9.81 * Math.cos(theta)),
            1 / 5
          );
          yn = Math.pow(
            (q * n * Math.pow(2 * Math.pow(1 + k * k, 0.5), 2 / 3)) /
              (Math.pow(so, 0.5) * Math.pow(k, 5 / 3)),
            3 / 8
          );
        } else {
          // ---- Sección trapecial / rectangular ----
          // Newton-Raphson para Yn (Manning)
          yn = 1;
          let pn1 = (q * n) / Math.pow(so, 0.5);
          let pn2 = 100;
          let safety = 0;
          while (Math.abs(pn1 - pn2) >= 0.00000001 && safety < 500) {
            const an = (b + k * yn) * yn;
            const pnv = b + 2 * yn * Math.pow(Math.pow(k, 2) + 1, 0.5);
            const rh = an / pnv;
            pn2 = an * Math.pow(rh, 2 / 3);
            const asl = b + 2 * k * yn;
            const pn3 =
              (5 / 3) * asl * Math.pow(Math.pow(rh, 2), 1 / 3) -
              (4 / 3) * Math.pow(rh, 5 / 3) * Math.pow(Math.pow(k, 2) + 1, 0.5);
            const ptn = (pn2 - pn1) / pn3;
            yn = yn - ptn;
            safety++;
          }

          // Newton-Raphson para Yc (energía específica)
          yc = 1;
          pn1 = Math.pow(q, 2) / (9.81 * Math.cos(theta));
          pn2 = 100;
          safety = 0;
          while (Math.abs(pn1 - pn2) >= 0.00000001 && safety < 500) {
            const an = (b + k * yc) * yc;
            const asl = b + 2 * k * yc;
            pn2 = Math.pow(an, 3) / asl;
            const pn3 =
              3 * Math.pow(an, 2) -
              (2 * k * Math.pow(an, 3)) / Math.pow(asl, 2);
            const ptn = (pn2 - pn1) / pn3;
            yc = yc - ptn;
            safety++;
          }
        }

        // Clasificación de régimen y perfil
        if (yn > yc) {
          regimen = 'Subcrítico';
          if (useRef && vtir < yn && vtir > yc) perfil = 'M2';
          else if (useRef && vtir < yn) perfil = 'M3';
          else if (useRef && vtir > yn) perfil = 'M1';
          else perfil = 'M';
        } else if (yn === yc) {
          regimen = 'Crítico';
          if (useRef && vtir > yn) perfil = 'C1';
          else if (useRef && vtir < yn) perfil = 'C3';
          else if (useRef && vtir === yn) perfil = 'C2';
          else perfil = 'C';
        } else {
          regimen = 'Supercrítico';
          if (useRef && vtir < yc && vtir > yn) perfil = 'S2';
          else if (useRef && vtir < yn) perfil = 'S3';
          else if (useRef && vtir > yc) perfil = 'S1';
          else perfil = 'S';
        }
      } else {
        // ---- Pendiente 0 o negativa ----
        yc = 1;
        let pn1 = Math.pow(q, 2) / (9.81 * Math.cos(theta));
        let pn2 = 100;
        let safety = 0;
        while (Math.abs(pn1 - pn2) >= 0.00000001 && safety < 500) {
          const an = (b + k * yc) * yc;
          const asl = b + 2 * k * yc;
          pn2 = Math.pow(an, 3) / asl;
          const pn3 =
            3 * Math.pow(an, 2) -
            (2 * k * Math.pow(an, 3)) / Math.pow(asl, 2);
          const ptn = (pn2 - pn1) / pn3;
          yc = yc - ptn;
          safety++;
        }
        yn = 0;

        if (so === 0) {
          regimen = 'Horizontal';
          if (useRef && vtir > yc) perfil = 'H2';
          else if (useRef && vtir < yc) perfil = 'H3';
          else if (useRef) {
            setError('Datos irreales para perfil H1');
            perfil = 'H1 No existe';
          } else perfil = 'H';
        } else {
          regimen = 'Adversa';
          if (useRef && vtir > yc) perfil = 'A2';
          else if (useRef && vtir < yc) perfil = 'A3';
          else if (useRef) {
            setError('Datos irreales para perfil A1');
            perfil = 'A1 No existe';
          } else perfil = 'A';
        }
      }

      if (!isFinite(yn) || !isFinite(yc)) {
        setError('No fue posible converger a una solución. Revise los datos ingresados.');
        return;
      }

      setResults({
        yn: Math.trunc(yn * 10000) / 10000,
        yc: Math.trunc(yc * 10000) / 10000,
        regimen,
        perfil,
        so: Math.trunc(so * 100000) / 100000
      });
    } catch (e) {
      setError('Error en el cálculo. Revise los datos ingresados.');
    }
  };

  const clear = () => {
    setInputs(initialInputs);
    setResults(null);
    setError(null);
  };

  const exportPNG = async () => {
    if (!exportRef.current) return;
    try {
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      const link = document.createElement('a');
      link.download = `flujo-gradualmente-variado-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl my-8 max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-rose-600 via-pink-600 to-fuchsia-600 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <Waves className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white leading-tight">
                Flujo Gradualmente Variado
              </h2>
              <p className="text-rose-100 text-sm">
                Trapecial, Rectangular y Triangular · HHTools
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto">
          <div ref={exportRef} className="bg-white">
            <div className="px-6 py-5">
              <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 flex gap-3 mb-6">
                <Info className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-rose-900 leading-relaxed">
                  Ingrese los datos del canal de sección trapecial, rectangular (K=0) o triangular (B=0).
                  Marque <strong>"Tirante de referencia"</strong> para determinar el perfil específico (M1/M2/M3, S1/S2/S3, etc.).
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Inputs */}
                <div className="space-y-4">
                  <h3 className="text-base font-bold text-gray-900 pb-2 border-b border-gray-200 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                    Datos de entrada
                  </h3>

                  <ChannelSectionDiagram
                    base={Number(inputs.B) || 0}
                    talud={Number(inputs.K) || 0}
                    tirante={results?.yn || 0}
                  />

                  <Field label="Q · Gasto o Caudal (m³/s)">
                    <input
                      type="number"
                      step="any"
                      value={inputs.Q}
                      onChange={e => handleChange('Q', e.target.value)}
                      className="input-style"
                      placeholder="Ej. 1.5"
                    />
                  </Field>

                  <Field label="B · Ancho de plantilla (m)">
                    <input
                      type="number"
                      step="any"
                      value={inputs.B}
                      onChange={e => handleChange('B', e.target.value)}
                      className="input-style"
                      placeholder="0 para sección triangular"
                    />
                  </Field>

                  <Field label="K · Talud (Adimensional)">
                    <input
                      type="number"
                      step="any"
                      value={inputs.K}
                      onChange={e => handleChange('K', e.target.value)}
                      className="input-style"
                      placeholder="0 para sección rectangular"
                    />
                  </Field>

                  <Field label="Material del canal">
                    <select
                      value={inputs.material}
                      onChange={e => handleMaterialChange(e.target.value)}
                      className="input-style"
                    >
                      <option value="">— Seleccione un material —</option>
                      {MATERIALS.map((m, i) => (
                        <option key={i} value={m.value}>
                          {m.label} (n = {m.value})
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="n · Rugosidad de Manning *">
                    <input
                      type="number"
                      step="any"
                      value={inputs.n}
                      onChange={e => handleChange('n', e.target.value)}
                      className="input-style"
                      placeholder="Ej. 0.013"
                    />
                  </Field>

                  <Field label="So · Pendiente (m/m)">
                    <input
                      type="number"
                      step="any"
                      value={inputs.So}
                      onChange={e => handleChange('So', e.target.value)}
                      className="input-style"
                      placeholder="Ej. 0.001"
                    />
                  </Field>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <input
                      type="checkbox"
                      id="useTirRef"
                      checked={inputs.useTirRef}
                      onChange={e => handleChange('useTirRef', e.target.checked)}
                      className="w-4 h-4 text-rose-600 rounded focus:ring-rose-500"
                    />
                    <label htmlFor="useTirRef" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                      Tirante de referencia
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={inputs.Tir}
                      onChange={e => handleChange('Tir', e.target.value)}
                      disabled={!inputs.useTirRef}
                      className="w-28 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:bg-gray-100 disabled:text-gray-400"
                      placeholder="m"
                    />
                  </div>

                  <p className="text-xs text-gray-500 italic">
                    * Puede ingresar el valor de n manualmente o seleccionar un material.
                  </p>
                </div>

                {/* Results */}
                <div className="space-y-4">
                  <h3 className="text-base font-bold text-gray-900 pb-2 border-b border-gray-200 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-fuchsia-500"></span>
                    Resultados
                  </h3>

                  <ResultField label="Tirante Normal · Yn (m)" value={results ? results.yn.toFixed(4) : ''} />
                  <ResultField label="Tirante Crítico · Yc (m)" value={results ? results.yc.toFixed(4) : ''} />
                  <ResultField label="Régimen" value={results ? results.regimen : ''} highlight />
                  <ResultField label="Perfil de Flujo" value={results ? results.perfil : ''} highlight />
                  <ResultField label="Pendiente de fricción · So (m/m)" value={results ? results.so.toFixed(5) : ''} />

                  <a
                    href="https://www.hidraulicafacil.com.mx/p/ayuda-perfiles-de-flujo.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-rose-600 hover:text-rose-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver tipos de perfiles de flujo
                  </a>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer brand for export */}
              <div className="mt-8 pt-4 border-t border-gray-200 text-center">
                <p className="text-xs text-gray-400">
                  Generado por openHH · HHTools — {new Date().toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-4 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={calculate}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
          >
            <Calculator className="w-4 h-4" />
            Calcular
          </button>
          <button
            onClick={clear}
            className="flex items-center gap-2 px-6 py-2.5 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold rounded-lg transition-colors"
          >
            <Eraser className="w-4 h-4" />
            Limpiar
          </button>
          <button
            onClick={exportPNG}
            disabled={!results}
            className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Exportar PNG
          </button>
        </div>
      </div>

      <style>{`
        .input-style {
          width: 100%;
          padding: 0.625rem 0.875rem;
          background-color: #ffffff;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          color: #111827;
          transition: all 0.15s ease;
        }
        .input-style:focus {
          outline: none;
          border-color: #e11d48;
          box-shadow: 0 0 0 3px rgba(225, 29, 72, 0.15);
        }
      `}</style>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    {children}
  </div>
);

const ResultField: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <input
      type="text"
      value={value}
      readOnly
      className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-mono ${
        highlight
          ? 'bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200 text-rose-900 font-bold'
          : 'bg-gray-50 border-gray-200 text-gray-900'
      }`}
      placeholder="—"
    />
  </div>
);

/**
 * Diagrama SVG dinamico de la seccion transversal del canal abierto.
 * Refleja en tiempo real B (base), K (talud) y el tirante normal (Yn calculado).
 * Conmuta automaticamente entre seccion triangular (B=0), rectangular (K=0) y trapecial.
 */
const ChannelSectionDiagram: React.FC<{ base: number; talud: number; tirante: number }> = ({
  base,
  talud,
  tirante,
}) => {
  const W = 360;
  const H = 200;
  const padX = 50;
  const baseY = H - 45;

  const tipo = base <= 0 ? 'Triangular' : talud <= 0 ? 'Rectangular' : 'Trapecial';

  const b = base > 0 ? base : 0;
  const k = talud > 0 ? talud : 0;
  const yRef = tirante > 0 ? tirante : 1;

  const topWidth = b + 2 * k * yRef;
  const maxWidth = Math.max(topWidth, b, 0.001);

  const usableW = W - padX * 2;
  const usableH = baseY - 40;
  const scaleX = usableW / maxWidth;
  const scaleY = usableH / yRef;
  const scale = Math.min(scaleX, scaleY);

  const cx = W / 2;
  const bottomHalf = (b * scale) / 2;
  const topHalf = (topWidth * scale) / 2;
  const waterDepth = yRef * scale;
  const waterTopY = baseY - waterDepth;

  const blX = cx - bottomHalf;
  const brX = cx + bottomHalf;
  const tlX = cx - topHalf;
  const trX = cx + topHalf;

  const fmt = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');

  const wallTopY = waterTopY - 16;
  const wallTlX = tlX - (k > 0 ? (16 / (scaleY || 1)) * k * scale : 0);
  const wallTrX = trX + (k > 0 ? (16 / (scaleY || 1)) * k * scale : 0);

  const waterPath = `M ${blX},${baseY} L ${tlX},${waterTopY} L ${trX},${waterTopY} L ${brX},${baseY} Z`;
  const channelPath = `M ${wallTlX},${wallTopY} L ${blX},${baseY} L ${brX},${baseY} L ${wallTrX},${wallTopY}`;

  return (
    <div className="bg-white rounded-lg border border-rose-200 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-3 py-2 bg-rose-50 border-b border-rose-100">
        <span className="text-xs font-semibold text-rose-800">Sección transversal del canal</span>
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white text-rose-700 border border-rose-300">
          {tipo}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="gvTrapWaterGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fb7185" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#c026d3" stopOpacity="0.9" />
          </linearGradient>
        </defs>

        <path d={channelPath} fill="none" stroke="#374151" strokeWidth="3" strokeLinejoin="round" />

        <path d={waterPath} fill="url(#gvTrapWaterGrad)" stroke="#a21caf" strokeWidth="1" />
        {topHalf > 6 && (
          <path
            d={`M ${tlX},${waterTopY} Q ${cx},${waterTopY - 3} ${trX},${waterTopY}`}
            fill="none"
            stroke="#ffffff"
            strokeWidth="1"
            opacity="0.7"
          />
        )}

        <g>
          <line x1={brX + 24} y1={waterTopY} x2={brX + 24} y2={baseY} stroke="#dc2626" strokeWidth="1.2" />
          <polygon points={`${brX + 24},${waterTopY} ${brX + 20},${waterTopY + 6} ${brX + 28},${waterTopY + 6}`} fill="#dc2626" />
          <polygon points={`${brX + 24},${baseY} ${brX + 20},${baseY - 6} ${brX + 28},${baseY - 6}`} fill="#dc2626" />
          <text x={brX + 30} y={(waterTopY + baseY) / 2 + 4} fill="#b91c1c" fontSize="11" fontWeight="700">
            {tirante > 0 ? `Yn=${fmt(tirante)}m` : 'Yn'}
          </text>
        </g>

        {b > 0 && (
          <g>
            <line x1={blX} y1={baseY + 16} x2={brX} y2={baseY + 16} stroke="#1d4ed8" strokeWidth="1.2" />
            <polygon points={`${blX},${baseY + 16} ${blX + 6},${baseY + 12} ${blX + 6},${baseY + 20}`} fill="#1d4ed8" />
            <polygon points={`${brX},${baseY + 16} ${brX - 6},${baseY + 12} ${brX - 6},${baseY + 20}`} fill="#1d4ed8" />
            <text x={cx} y={baseY + 30} textAnchor="middle" fill="#1e40af" fontSize="11" fontWeight="700">
              B = {fmt(base)} m
            </text>
          </g>
        )}

        {k > 0 && tirante >= 0 && (
          <text x={tlX - 6} y={(waterTopY + baseY) / 2} textAnchor="end" fill="#a21caf" fontSize="10" fontWeight="700">
            K = {fmt(talud)}
          </text>
        )}
      </svg>
    </div>
  );
};

export default FlujoGradualmenteVariadoCalculator;
